/**
 * AdminDashboard: staff console (Phase D: Admin Console).
 *
 * Staff only: anyone else is sent to the staff sign-in. The API enforces the
 * same rule on every staff endpoint; this guard just keeps the page honest.
 * Shows the tenant configuration summary from GET /v1/tenant, district map
 * management, and placeholder cards for modules that land later.
 */
import { Navigate } from 'react-router-dom';
import { PageShell } from '../components/Layout';
import { isStaffRole } from '../lib/auth';
import { displayNameFor, useAuthStore } from '../stores/auth';
import { useTenantStore } from '../stores/tenant';
import { DistrictMapsAdmin } from '../components/admin/DistrictMapsAdmin';

const MODULE_PLACEHOLDERS = [
  {
    title: 'Moderation queue',
    description: 'Review community-of-interest submissions and public plan proposals.',
    todo: 'GET /v1/cois?state=submitted, POST /v1/cois/{id}/moderate',
  },
  {
    title: 'Plan review',
    description: 'Compare submitted plans, inspect validation results, publish staff reports.',
    todo: 'GET /v1/plans, POST /v1/plans/{id}/validate',
  },
  {
    title: 'Dataset & tiles',
    description: 'Dataset release, PMTiles build status, and tile health for this tenant.',
    todo: 'Data-build job status endpoint (backend worker)',
  },
  {
    title: 'Audit events',
    description: 'Authentication, moderation, and configuration-change audit trail.',
    todo: 'GET /v1/admin/audit-events',
  },
];

export function AdminDashboard(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  document.title = `Admin — ${tenant?.name ?? 'CivicLines'}`;

  if (!initialized) {
    return (
      <PageShell title="Admin dashboard">
        <p className="text-sm text-stone-500" role="status">
          Checking your session…
        </p>
      </PageShell>
    );
  }
  if (!user || !isStaffRole(user.role)) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <PageShell title="Admin dashboard">
      <p className="text-sm text-stone-600">
        Signed in as <span className="font-medium text-ink">{displayNameFor(user)}</span> ({user.email},{' '}
        {user.role}).
      </p>

      <section className="mt-6 rounded-lg bg-white p-6 shadow-sm ring-1 ring-stone-200" aria-label="Tenant configuration">
        <h2 className="text-lg font-semibold text-ink">Tenant configuration</h2>
        {tenant ? (
          <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div><dt className="text-stone-500">Tenant ID</dt><dd className="font-mono">{tenant.tenant_id}</dd></div>
            <div><dt className="text-stone-500">Slug</dt><dd className="font-mono">{tenant.slug}</dd></div>
            <div><dt className="text-stone-500">Config version</dt><dd className="font-mono">{tenant.config_version}</dd></div>
            <div><dt className="text-stone-500">Districts</dt><dd>{tenant.districts.count}</dd></div>
            <div><dt className="text-stone-500">Block id property</dt><dd className="font-mono">{tenant.map.id_property}</dd></div>
            <div><dt className="text-stone-500">Source layer</dt><dd className="font-mono">{tenant.map.source_layer}</dd></div>
            <div><dt className="text-stone-500">Drawing</dt><dd>{tenant.features.drawing_enabled ? 'enabled' : 'disabled'}</dd></div>
            <div><dt className="text-stone-500">AI sketch</dt><dd>{tenant.features.ai_sketch_enabled ? 'enabled' : 'disabled'}</dd></div>
            <div><dt className="text-stone-500">COI intake</dt><dd>{tenant.features.coi_enabled ? 'enabled' : 'disabled'}</dd></div>
          </dl>
        ) : (
          <p className="mt-3 text-sm text-stone-500">No tenant configuration loaded.</p>
        )}
      </section>

      <DistrictMapsAdmin />

      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        {MODULE_PLACEHOLDERS.map((module) => (
          <section
            key={module.title}
            className="rounded-lg border border-dashed border-stone-300 bg-white p-6"
            aria-label={`${module.title} (placeholder)`}
          >
            <h2 className="text-lg font-semibold text-ink">{module.title}</h2>
            <p className="mt-1 text-sm text-stone-600">{module.description}</p>
            <p className="mt-3 font-mono text-xs text-stone-400">TODO: {module.todo}</p>
          </section>
        ))}
      </div>
    </PageShell>
  );
}
